import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, Globe } from 'lucide-react';
import { LOGO_DARK } from '../App';
import { useTranslation } from '../context/LanguageContext';
import { LANGUAGES, useLanguage } from '../context/LanguageContext';
import { API_URL } from '../config/api';

// Default Cookie Policy content per language. Mirrors the same
// pattern used by Privacy.js / Terms.js — backend (settings.legal_content)
// can override via `cookies_<lang>` keys; if not present, we fall back
// to the static HTML below.
//
// Compliance note (IT/EU — Direttiva ePrivacy + Linee guida Garante 10/06/2021):
// We list 3 categories (technical, analytics, profiling). Technical
// cookies do NOT require consent; the other two do, and the consent
// choice is collected by the CookieConsent banner on the landing.
const CONTENT = {
  it: `
<h1 class="text-2xl font-bold text-gray-900 mb-2">Informativa Cookie</h1>
<p class="text-sm text-gray-500 mb-6">Ultimo aggiornamento: Febbraio 2026</p>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">1. Cosa sono i cookie</h2>
<p class="text-gray-700 leading-relaxed">I cookie sono piccoli file di testo che i siti visitati salvano sul tuo dispositivo per ricordare preferenze, autenticazione e statistiche di utilizzo.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">2. Tipologie di cookie utilizzati</h2>
<ul class="list-disc pl-6 text-gray-700 space-y-2">
<li><strong>Cookie tecnici (necessari):</strong> indispensabili per il funzionamento del sito (sessione, autenticazione, lingua, preferenze UI). Non richiedono consenso.</li>
<li><strong>Cookie analitici:</strong> raccolgono in forma aggregata informazioni sull'uso del sito (pagine visitate, durata, errori) per migliorare il servizio. Attivati solo dopo consenso.</li>
<li><strong>Cookie di profilazione/marketing:</strong> utilizzati per mostrare contenuti personalizzati o misurare campagne. Attivati solo dopo consenso.</li>
</ul></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">3. Base giuridica</h2>
<p class="text-gray-700 leading-relaxed">I cookie tecnici si basano sul legittimo interesse del Titolare (art. 6.1.f GDPR). I cookie analitici e di profilazione richiedono il tuo consenso esplicito (art. 6.1.a GDPR e art. 122 D. Lgs. 196/2003).</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">4. Come gestire il consenso</h2>
<p class="text-gray-700 leading-relaxed">Al primo accesso ti viene mostrato un banner per accettare, rifiutare o personalizzare la scelta. Puoi modificare la tua preferenza in qualsiasi momento cancellando i dati del sito dal browser, oppure ripristinando le impostazioni di consenso dal banner.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">5. Cookie di terze parti</h2>
<p class="text-gray-700 leading-relaxed">Alcuni servizi integrati (es. provider di analytics, video, pagamenti) possono installare cookie propri. Ti invitiamo a consultare le loro informative.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">6. Durata</h2>
<p class="text-gray-700 leading-relaxed">I cookie di sessione si cancellano alla chiusura del browser. I cookie persistenti hanno una durata massima di 12 mesi.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">7. Diritti dell'interessato</h2>
<p class="text-gray-700 leading-relaxed">Puoi esercitare i diritti previsti dagli artt. 15–22 GDPR (accesso, rettifica, cancellazione, opposizione, portabilità) scrivendo a <strong>privacy@slimmy.app</strong>.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">8. Contatti</h2>
<p class="text-gray-700 leading-relaxed">Per qualsiasi richiesta relativa ai cookie: <strong>privacy@slimmy.app</strong></p></section>
`,
  en: `
<h1 class="text-2xl font-bold text-gray-900 mb-2">Cookie Policy</h1>
<p class="text-sm text-gray-500 mb-6">Last updated: February 2026</p>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">1. What cookies are</h2>
<p class="text-gray-700 leading-relaxed">Cookies are small text files saved on your device by the sites you visit, used to remember preferences, authentication and usage statistics.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">2. Types of cookies we use</h2>
<ul class="list-disc pl-6 text-gray-700 space-y-2">
<li><strong>Technical (necessary) cookies:</strong> required for the site to work (session, authentication, language, UI preferences). No consent required.</li>
<li><strong>Analytics cookies:</strong> aggregate information about how the site is used (pages, duration, errors) to improve it. Activated only after your consent.</li>
<li><strong>Profiling / marketing cookies:</strong> used to show personalized content or measure campaigns. Activated only after your consent.</li>
</ul></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">3. Legal basis</h2>
<p class="text-gray-700 leading-relaxed">Technical cookies rely on the legitimate interest of the Controller (Art. 6.1.f GDPR). Analytics and profiling cookies require your explicit consent (Art. 6.1.a GDPR and the EU ePrivacy Directive).</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">4. How to manage consent</h2>
<p class="text-gray-700 leading-relaxed">On your first visit a banner lets you accept, reject or customize. You can change your preference any time by clearing site data in your browser or by restoring consent settings from the banner.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">5. Third-party cookies</h2>
<p class="text-gray-700 leading-relaxed">Some integrated services (e.g. analytics, video, payment providers) may set their own cookies. Please refer to their respective policies.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">6. Retention</h2>
<p class="text-gray-700 leading-relaxed">Session cookies are deleted when you close the browser. Persistent cookies last up to 12 months.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">7. Your rights</h2>
<p class="text-gray-700 leading-relaxed">You may exercise the rights under Arts. 15–22 GDPR (access, rectification, erasure, objection, portability) by writing to <strong>privacy@slimmy.app</strong>.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">8. Contact</h2>
<p class="text-gray-700 leading-relaxed">For any request relating to cookies: <strong>privacy@slimmy.app</strong></p></section>
`,
  es: `
<h1 class="text-2xl font-bold text-gray-900 mb-2">Política de Cookies</h1>
<p class="text-sm text-gray-500 mb-6">Última actualización: Febrero 2026</p>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">1. Qué son las cookies</h2>
<p class="text-gray-700 leading-relaxed">Las cookies son pequeños archivos de texto que los sitios web guardan en tu dispositivo para recordar preferencias, autenticación y estadísticas de uso.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">2. Tipos de cookies que utilizamos</h2>
<ul class="list-disc pl-6 text-gray-700 space-y-2">
<li><strong>Cookies técnicas (necesarias):</strong> indispensables para el funcionamiento del sitio (sesión, autenticación, idioma, preferencias). No requieren consentimiento.</li>
<li><strong>Cookies analíticas:</strong> recogen de forma agregada información sobre el uso del sitio para mejorarlo. Se activan solo con tu consentimiento.</li>
<li><strong>Cookies de perfilado / marketing:</strong> usadas para mostrar contenidos personalizados o medir campañas. Se activan solo con tu consentimiento.</li>
</ul></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">3. Base legal</h2>
<p class="text-gray-700 leading-relaxed">Las cookies técnicas se basan en el interés legítimo del Responsable (art. 6.1.f RGPD). Las cookies analíticas y de perfilado requieren tu consentimiento explícito (art. 6.1.a RGPD).</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">4. Cómo gestionar el consentimiento</h2>
<p class="text-gray-700 leading-relaxed">En tu primera visita se muestra un banner para aceptar, rechazar o personalizar. Puedes cambiar tu preferencia en cualquier momento borrando los datos del sitio en tu navegador.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">5. Cookies de terceros</h2>
<p class="text-gray-700 leading-relaxed">Algunos servicios integrados pueden instalar sus propias cookies. Consulta sus respectivas políticas.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">6. Duración</h2>
<p class="text-gray-700 leading-relaxed">Las cookies de sesión se eliminan al cerrar el navegador. Las cookies persistentes duran un máximo de 12 meses.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">7. Tus derechos</h2>
<p class="text-gray-700 leading-relaxed">Puedes ejercer los derechos de los arts. 15–22 RGPD escribiendo a <strong>privacy@slimmy.app</strong>.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">8. Contacto</h2>
<p class="text-gray-700 leading-relaxed">Para cualquier solicitud sobre cookies: <strong>privacy@slimmy.app</strong></p></section>
`,
  pt: `
<h1 class="text-2xl font-bold text-gray-900 mb-2">Política de Cookies</h1>
<p class="text-sm text-gray-500 mb-6">Última atualização: Fevereiro 2026</p>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">1. O que são cookies</h2>
<p class="text-gray-700 leading-relaxed">Cookies são pequenos arquivos de texto que os sites salvam no seu dispositivo para lembrar preferências, autenticação e estatísticas de uso.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">2. Tipos de cookies que usamos</h2>
<ul class="list-disc pl-6 text-gray-700 space-y-2">
<li><strong>Cookies técnicos (necessários):</strong> indispensáveis para o funcionamento do site. Não requerem consentimento.</li>
<li><strong>Cookies analíticos:</strong> coletam informações agregadas sobre o uso do site. Ativados apenas com seu consentimento.</li>
<li><strong>Cookies de perfilamento / marketing:</strong> usados para mostrar conteúdo personalizado ou medir campanhas. Ativados apenas com seu consentimento.</li>
</ul></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">3. Base legal</h2>
<p class="text-gray-700 leading-relaxed">Cookies técnicos baseiam-se no interesse legítimo do Controlador (art. 6.1.f RGPD/LGPD). Cookies analíticos e de perfilamento exigem consentimento explícito.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">4. Como gerenciar o consentimento</h2>
<p class="text-gray-700 leading-relaxed">No primeiro acesso, um banner permite aceitar, recusar ou personalizar. Você pode alterar sua preferência limpando os dados do site no navegador.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">5. Cookies de terceiros</h2>
<p class="text-gray-700 leading-relaxed">Alguns serviços integrados podem instalar cookies próprios. Consulte suas políticas.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">6. Duração</h2>
<p class="text-gray-700 leading-relaxed">Cookies de sessão são excluídos ao fechar o navegador. Cookies persistentes duram no máximo 12 meses.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">7. Seus direitos</h2>
<p class="text-gray-700 leading-relaxed">Você pode exercer os direitos dos arts. 15–22 RGPD escrevendo para <strong>privacy@slimmy.app</strong>.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">8. Contato</h2>
<p class="text-gray-700 leading-relaxed">Para qualquer pedido sobre cookies: <strong>privacy@slimmy.app</strong></p></section>
`,
  de: `
<h1 class="text-2xl font-bold text-gray-900 mb-2">Cookie-Richtlinie</h1>
<p class="text-sm text-gray-500 mb-6">Letzte Aktualisierung: Februar 2026</p>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">1. Was Cookies sind</h2>
<p class="text-gray-700 leading-relaxed">Cookies sind kleine Textdateien, die von besuchten Websites auf Ihrem Gerät gespeichert werden, um Einstellungen, Authentifizierung und Nutzungsstatistiken zu speichern.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">2. Verwendete Cookie-Arten</h2>
<ul class="list-disc pl-6 text-gray-700 space-y-2">
<li><strong>Technische (notwendige) Cookies:</strong> erforderlich für den Betrieb der Website. Keine Einwilligung nötig.</li>
<li><strong>Analyse-Cookies:</strong> aggregierte Informationen zur Nutzung der Website. Erst nach Ihrer Einwilligung aktiv.</li>
<li><strong>Profiling- / Marketing-Cookies:</strong> für personalisierte Inhalte oder Kampagnenmessung. Erst nach Ihrer Einwilligung aktiv.</li>
</ul></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">3. Rechtsgrundlage</h2>
<p class="text-gray-700 leading-relaxed">Technische Cookies stützen sich auf das berechtigte Interesse (Art. 6.1.f DSGVO). Analyse- und Profiling-Cookies erfordern Ihre ausdrückliche Einwilligung (Art. 6.1.a DSGVO).</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">4. Einwilligung verwalten</h2>
<p class="text-gray-700 leading-relaxed">Beim ersten Besuch zeigt ein Banner die Optionen Annehmen, Ablehnen oder Anpassen. Sie können Ihre Auswahl jederzeit ändern, indem Sie die Website-Daten im Browser löschen.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">5. Cookies von Drittanbietern</h2>
<p class="text-gray-700 leading-relaxed">Einige eingebundene Dienste können eigene Cookies setzen. Bitte beachten Sie deren Datenschutzhinweise.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">6. Speicherdauer</h2>
<p class="text-gray-700 leading-relaxed">Sitzungs-Cookies werden beim Schließen des Browsers gelöscht. Dauerhafte Cookies bleiben höchstens 12 Monate gespeichert.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">7. Ihre Rechte</h2>
<p class="text-gray-700 leading-relaxed">Sie können die Rechte nach Art. 15–22 DSGVO ausüben unter <strong>privacy@slimmy.app</strong>.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">8. Kontakt</h2>
<p class="text-gray-700 leading-relaxed">Für Anfragen zu Cookies: <strong>privacy@slimmy.app</strong></p></section>
`,
  fr: `
<h1 class="text-2xl font-bold text-gray-900 mb-2">Politique de Cookies</h1>
<p class="text-sm text-gray-500 mb-6">Dernière mise à jour : Février 2026</p>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">1. Qu'est-ce qu'un cookie</h2>
<p class="text-gray-700 leading-relaxed">Les cookies sont de petits fichiers texte enregistrés sur votre appareil par les sites que vous visitez pour mémoriser préférences, authentification et statistiques d'utilisation.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">2. Types de cookies utilisés</h2>
<ul class="list-disc pl-6 text-gray-700 space-y-2">
<li><strong>Cookies techniques (nécessaires) :</strong> indispensables au fonctionnement du site. Aucun consentement requis.</li>
<li><strong>Cookies analytiques :</strong> collectent des informations agrégées sur l'utilisation du site. Activés uniquement avec votre consentement.</li>
<li><strong>Cookies de profilage / marketing :</strong> utilisés pour afficher du contenu personnalisé ou mesurer les campagnes. Activés uniquement avec votre consentement.</li>
</ul></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">3. Base légale</h2>
<p class="text-gray-700 leading-relaxed">Les cookies techniques reposent sur l'intérêt légitime du Responsable (art. 6.1.f RGPD). Les cookies analytiques et de profilage nécessitent votre consentement explicite (art. 6.1.a RGPD).</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">4. Gérer le consentement</h2>
<p class="text-gray-700 leading-relaxed">Lors de votre première visite, un bandeau permet d'accepter, refuser ou personnaliser. Vous pouvez modifier votre choix à tout moment en effaçant les données du site dans votre navigateur.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">5. Cookies tiers</h2>
<p class="text-gray-700 leading-relaxed">Certains services intégrés peuvent installer leurs propres cookies. Veuillez consulter leurs politiques.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">6. Durée</h2>
<p class="text-gray-700 leading-relaxed">Les cookies de session sont supprimés à la fermeture du navigateur. Les cookies persistants ont une durée maximale de 12 mois.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">7. Vos droits</h2>
<p class="text-gray-700 leading-relaxed">Vous pouvez exercer les droits prévus aux art. 15–22 RGPD en écrivant à <strong>privacy@slimmy.app</strong>.</p></section>

<section><h2 class="text-xl font-semibold text-gray-900 mb-3">8. Contact</h2>
<p class="text-gray-700 leading-relaxed">Pour toute demande relative aux cookies : <strong>privacy@slimmy.app</strong></p></section>
`
};

export default function Cookies() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      // Try backend override first (legal_content.cookies_<lang>), fall
      // back to the static HTML above so the page is never empty even
      // if admin hasn't seeded the DB yet.
      try {
        const res = await fetch(`${API_URL}/api/legal/cookies?lang=${language}`);
        if (res.ok) {
          const data = await res.json();
          if (data?.content) { setContent(data.content); return; }
        }
      } catch (_) { /* fallthrough to default */ }
      setContent(CONTENT[language] || CONTENT.en);
    };
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [language]);

  return (
    <div className="fixed inset-0 bg-white overflow-y-auto">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="cookies-back-btn"
          >
            <ChevronLeft size={24} className="text-gray-700" />
          </button>
          <img src={LOGO_DARK} alt="Slimmy" className="h-8" />
          <div className="ml-auto relative">
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-sm text-gray-700 transition-colors"
              data-testid="cookies-lang-btn"
              aria-label={t('legal.language') || 'Language'}
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-medium">{language}</span>
            </button>
            {langOpen && (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-20 cursor-default"
                  onClick={() => setLangOpen(false)}
                  aria-label="close"
                  tabIndex={-1}
                />
                <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-30" data-testid="cookies-lang-menu">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => { setLanguage(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 ${
                        l.code === language ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-700'
                      }`}
                      data-testid={`cookies-lang-${l.code}`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="prose prose-gray max-w-none space-y-6" dangerouslySetInnerHTML={{ __html: content }} />
        )}
        <div className="mt-12 pt-6 pb-12 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center">
            © 2026 Slimmy.com - {t('legal.allRightsReserved')}
          </p>
        </div>
      </div>
    </div>
  );
}
